import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, Phone, Grid3x3, List, Plus, MoreVertical, Pencil, Trash2, KeyRound, UserCheck, UserX } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { StaffLoginDialog } from '../components/team/StaffLoginDialog';
import { LoginAccountsPanel } from '../components/team/LoginAccountsPanel';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const shiftLabels = {
  'day': '🌅 Day',
  'swing': '☀️ Swing',
  'evening': '🌆 Evening',
  'overnight': '🌙 Overnight'
};

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  // Login account management (admin-only)
  const [accounts, setAccounts] = useState({});
  const [loginDialog, setLoginDialog] = useState(null); // { mode, member }
  let isAdmin = false;
  try { isAdmin = JSON.parse(localStorage.getItem('currentUser'))?.role?.trim?.().toLowerCase?.() === 'admin'; } catch (_) {}
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    staff_type: 'ft',
    preferred_shifts: [],
    availability_notes: '',
    skills: '',
    max_hours_per_week: 40,
    is_active: true
  });

  // Fetch team members from API
  useEffect(() => {
    fetchTeamMembers();
  }, [filterStatus]);

  useEffect(() => {
    if (isAdmin) fetchAccounts();
    // eslint-disable-next-line
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounts/status`);
      setAccounts(res.data);
    } catch (err) {
      console.error('accounts fetch failed', err);
    }
  };

  const handleAccountStatus = async (member, isActive) => {
    try {
      await axios.put(`${API_URL}/api/accounts/${member.id}/status`, { is_active: isActive });
      toast.success(`Login ${isActive ? 'activated' : 'deactivated'} for ${member.name}`);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const activeOnly = filterStatus === 'active';
      const response = await axios.get(`${API_URL}/api/team?active_only=${activeOnly}`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name,
      email: member.email || '',
      phone: member.phone || '',
      staff_type: member.staff_type,
      preferred_shifts: member.preferred_shifts || [],
      availability_notes: member.availability_notes || '',
      skills: (member.skills || []).join(', '),
      max_hours_per_week: member.max_hours_per_week,
      is_active: member.is_active
    });
    setIsEditDialogOpen(true);
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      staff_type: 'ft',
      preferred_shifts: [],
      availability_notes: '',
      skills: '',
      max_hours_per_week: 40,
      is_active: true
    });
    setIsAddDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        role: 'RSS',
        department: '3.1 Program',
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s)
      };

      if (selectedMember) {
        // Update existing member
        await axios.put(`${API_URL}/api/team/${selectedMember.id}`, payload);
      } else {
        // Create new member
        await axios.post(`${API_URL}/api/team`, payload);
      }

      fetchTeamMembers();
      setIsEditDialogOpen(false);
      setIsAddDialogOpen(false);
      setSelectedMember(null);
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member. Please try again.');
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name}? This will deactivate their account.`)) {
      return;
    }

    try {
      // Deactivate instead of delete
      await axios.put(`${API_URL}/api/team/${member.id}`, {
        ...member,
        is_active: false
      });
      fetchTeamMembers();
    } catch (error) {
      console.error('Error removing team member:', error);
      alert('Failed to remove team member. Please try again.');
    }
  };

  const toggleShift = (shift) => {
    const current = formData.preferred_shifts || [];
    const updated = current.includes(shift)
      ? current.filter(s => s !== shift)
      : [...current, shift];
    setFormData({ ...formData, preferred_shifts: updated });
  };

  const loginBadge = (memberId) => {
    if (!isAdmin) return null;
    const acc = accounts[memberId];
    if (!acc) {
      return <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`login-badge-${memberId}`}>No login</Badge>;
    }
    if (acc.status === 'active') {
      return (
        <Badge className="text-[10px] bg-[#1D9E75] hover:bg-[#1D9E75]" data-testid={`login-badge-${memberId}`}>
          Active login{acc.pending_setup ? ' · setup pending' : ''}
        </Badge>
      );
    }
    return <Badge variant="secondary" className="text-[10px]" data-testid={`login-badge-${memberId}`}>Inactive login</Badge>;
  };

  const loginMenuItems = (member) => {
    if (!isAdmin) return null;
    const acc = accounts[member.id];
    if (!acc) {
      return (
        <DropdownMenuItem onClick={() => setLoginDialog({ mode: 'create', member })} data-testid={`create-login-${member.id}`}>
          <KeyRound className="h-4 w-4 mr-2" />
          Create Staff Login
        </DropdownMenuItem>
      );
    }
    return (
      <>
        <DropdownMenuItem onClick={() => setLoginDialog({ mode: 'reset', member, account: acc })} data-testid={`reset-password-${member.id}`}>
          <KeyRound className="h-4 w-4 mr-2" />
          Reset Password
        </DropdownMenuItem>
        {acc.status === 'active' ? (
          <DropdownMenuItem onClick={() => handleAccountStatus(member, false)} className="text-red-600" data-testid={`deactivate-login-${member.id}`}>
            <UserX className="h-4 w-4 mr-2" />
            Deactivate Login
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => handleAccountStatus(member, true)} data-testid={`activate-login-${member.id}`}>
            <UserCheck className="h-4 w-4 mr-2" />
            Activate Login
          </DropdownMenuItem>
        )}
      </>
    );
  };

  // Get initials from name
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get preferred shifts display
  const getPreferredShiftsDisplay = (preferredShifts) => {
    if (!preferredShifts || preferredShifts.length === 0) return 'No preference';
    if (preferredShifts.length === 4) return '⭐ Flex (All shifts)';
    return preferredShifts.map(s => shiftLabels[s] || s).join(', ');
  };

  // Filter team members
  const filteredTeam = teamMembers.filter((member) =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (member.department && member.department.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">
            3.1 Program RSS Staff - {filteredTeam.length} members
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid3x3 className="h-4 w-4 mr-1" />
            Grid
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
        </div>
      </div>

      {/* Filters & Search */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, role, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          
          <Tabs value={filterStatus} onValueChange={setFilterStatus} className="w-auto">
            <TabsList>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </Card>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeam.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="p-5 hover:shadow-lg transition-shadow relative">
                {/* Three-dot menu */}
                <div className="absolute top-4 right-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-background">
                      <DropdownMenuItem onClick={() => handleEdit(member)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {loginMenuItems(member)}
                      <DropdownMenuItem 
                        onClick={() => handleDelete(member)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-start gap-4">
                  <Avatar className="h-14 w-14">
                    <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2 pr-8">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base truncate">{member.name}</h3>
                        <p className="text-sm text-muted-foreground">{member.role}</p>
                        <div className="mt-1">{loginBadge(member.id)}</div>
                      </div>
                      <Badge variant={member.is_active ? 'default' : 'secondary'} className="ml-2">
                        {member.staff_type.toUpperCase()}
                      </Badge>
                    </div>

                    {member.department && (
                      <div className="text-xs text-muted-foreground mb-2">
                        {member.department}
                      </div>
                    )}

                    <div className="space-y-1 text-sm">
                      {member.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate text-xs">{member.email}</span>
                        </div>
                      )}
                      {member.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          <span className="text-xs">{member.phone}</span>
                        </div>
                      )}
                      {!member.email && !member.phone && (
                        <div className="text-xs text-muted-foreground italic">
                          No contact info on file
                        </div>
                      )}
                    </div>

                    {/* Preferred Shifts */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-xs font-medium text-muted-foreground mb-1">
                        Preferred Shifts:
                      </div>
                      <div className="text-xs">
                        {getPreferredShiftsDisplay(member.preferred_shifts)}
                      </div>
                    </div>

                    {/* Availability Notes */}
                    {member.availability_notes && (
                      <div className="mt-2 text-xs italic text-muted-foreground bg-muted px-2 py-1 rounded">
                        {member.availability_notes}
                      </div>
                    )}

                    {/* Skills */}
                    {member.skills && member.skills.length > 0 && (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-1">
                          {member.skills.slice(0, 3).map((skill, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs px-1 py-0">
                              {skill}
                            </Badge>
                          ))}
                          {member.skills.length > 3 && (
                            <Badge variant="outline" className="text-xs px-1 py-0">
                              +{member.skills.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Max Hours */}
                    <div className="mt-2 text-xs text-muted-foreground">
                      Max: {member.max_hours_per_week}h/week
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Preferred Shifts</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Max Hours</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTeam.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{member.name}</div>
                        {member.department && (
                          <div className="text-xs text-muted-foreground">{member.department}</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{member.role}</div>
                    <div className="mt-0.5">{loginBadge(member.id)}</div>
                    {member.availability_notes && (
                      <div className="text-xs text-muted-foreground italic">{member.availability_notes}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={member.is_active ? 'default' : 'secondary'}>
                      {member.staff_type.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {getPreferredShiftsDisplay(member.preferred_shifts)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {member.email || <span className="italic">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {member.phone || <span className="italic">—</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{member.max_hours_per_week}h</div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-background">
                        <DropdownMenuItem onClick={() => handleEdit(member)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {loginMenuItems(member)}
                        <DropdownMenuItem 
                          onClick={() => handleDelete(member)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Empty State */}
      {filteredTeam.length === 0 && (
        <Card className="p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No team members found</h3>
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? 'Try adjusting your search query'
              : 'No team members available'}
          </p>
        </Card>
      )}

      {/* Edit/Add Dialog */}
      <Dialog open={isEditDialogOpen || isAddDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        setIsAddDialogOpen(open);
        if (!open) setSelectedMember(null);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedMember ? 'Edit' : 'Add'} Team Member</DialogTitle>
            <DialogDescription>
              {selectedMember ? 'Update' : 'Create'} RSS staff member information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(804) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Employment Type</label>
                <Select
                  value={formData.staff_type}
                  onValueChange={(value) => setFormData({ ...formData, staff_type: value })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background">
                    <SelectItem value="ft">Full-Time (FT)</SelectItem>
                    <SelectItem value="prn">PRN (As-Needed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Max Hours/Week</label>
                <Input
                  type="number"
                  value={formData.max_hours_per_week}
                  onChange={(e) => setFormData({ ...formData, max_hours_per_week: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Preferred Shifts</label>
              <div className="flex flex-wrap gap-2">
                {['day', 'swing', 'evening', 'overnight'].map(shift => (
                  <Button
                    key={shift}
                    type="button"
                    variant={(formData.preferred_shifts || []).includes(shift) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleShift(shift)}
                  >
                    {shiftLabels[shift]}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Availability Notes</label>
              <Input
                value={formData.availability_notes}
                onChange={(e) => setFormData({ ...formData, availability_notes: e.target.value })}
                placeholder="e.g., Day shift 7am-3pm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Skills (comma-separated)</label>
              <Textarea
                value={formData.skills}
                onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                placeholder="Crisis Intervention, Client Support, Documentation"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Active</label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditDialogOpen(false);
              setIsAddDialogOpen(false);
              setSelectedMember(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {selectedMember ? 'Save Changes' : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StaffLoginDialog
        open={!!loginDialog}
        onOpenChange={(o) => !o && setLoginDialog(null)}
        mode={loginDialog?.mode}
        member={loginDialog?.member}
        account={loginDialog?.account}
        onDone={fetchAccounts}
      />

      {isAdmin && <LoginAccountsPanel />}
    </div>
  );
};

export default Team;
